/**
 * `/kb/$workspaceId` — a single workspace, split into Documents / Ask tabs in the
 * PageHeader (no more single endless column). Documents: stat strip + compact
 * dropzone side-by-side, then a dense library table. Ask: center answer + a
 * digested source rail, with the source-viewer drawer.
 *
 * The citation spine lives here: `openCitation` is the ONE handler wired to the
 * answer's inline markers AND the rail's "View in document" — it opens the drawer
 * at the cited chunk and activates the matching source card.
 */
import { Button } from "@d12labs/ui/button";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { PageHeader, PageTab } from "@d12labs/ui/page-header";
import { Pill } from "@d12labs/ui/pill";
import { Skeleton } from "@d12labs/ui/skeleton";
import { Toolbar } from "@d12labs/ui/toolbar";
import { Link, useParams } from "@tanstack/react-router";
import {
	ArrowLeft,
	FileText,
	Layers,
	Library,
	List,
	Loader2,
	Search,
	Sparkles,
	UploadCloud,
} from "lucide-react";
import { useEffect, useState } from "react";
import { AskPanel } from "../components/ask-panel";
import { DocumentTable } from "../components/document-table";
import { SourceDrawer } from "../components/source-drawer";
import { SourceRail } from "../components/source-rail";
import { UploadDropzone } from "../components/upload-dropzone";
import {
	useAskQuery,
	useDeleteDocument,
	useDocuments,
	useWorkspaceStats,
	useWorkspaces,
} from "../hooks/use-kb";
import { DOC_TYPE_META, deriveAnswer, timeAgo } from "../kb-ui";
import { type DocType, isInFlight, type KbCitation, type KbDocument } from "../types";

type Tab = "docs" | "ask";
type TypeFilter = "all" | "transcript" | "notes" | "pdf";
type DrawerState = { documentId: string; chunkIndex: number; score?: number };

const TYPE_FILTERS: { key: TypeFilter; label: string }[] = [
	{ key: "all", label: "All" },
	{ key: "transcript", label: "Transcripts" },
	{ key: "notes", label: "Notes" },
	{ key: "pdf", label: "PDF" },
];

function StatCard({
	icon: Icon,
	label,
	value,
	sub,
}: {
	icon: typeof FileText;
	label: string;
	value: string | number;
	sub: string;
}) {
	return (
		<div className="rounded-lg border border-hairline bg-surface-1 p-3.5">
			<div className="flex items-center gap-1.5 text-ink-tertiary">
				<Icon className="size-3.5" />
				<span className="text-eyebrow uppercase">{label}</span>
			</div>
			<div className="mt-1.5 text-2xl font-semibold tabular-nums text-foreground">{value}</div>
			<div className="mt-0.5 truncate text-meta text-ink-subtle">{sub}</div>
		</div>
	);
}

export function WorkspacePage() {
	const { workspaceId } = useParams({ strict: false }) as { workspaceId: string };

	const { data: workspaces, isLoading: workspacesLoading } = useWorkspaces();
	const workspace = workspaces?.find((w) => w.id === workspaceId);
	const { data: documents, isLoading: documentsLoading } = useDocuments(workspaceId);
	const deleteDocument = useDeleteDocument(workspaceId);
	const anyInFlight = documents?.items.some((d) => isInFlight(d.status)) ?? false;
	const { data: stats } = useWorkspaceStats(workspaceId, { live: anyInFlight });

	const [tab, setTab] = useState<Tab>("docs");

	// ── Documents tab filters ──
	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

	// ── Ask + citation-spine state ──
	const ask = useAskQuery(workspaceId);
	const [question, setQuestion] = useState("");
	const [lastAsked, setLastAsked] = useState("");
	const [recent, setRecent] = useState<string[]>([]);
	const [activeCite, setActiveCite] = useState<number | null>(null);
	const [drawer, setDrawer] = useState<DrawerState | null>(null);

	// ⌘K → Ask tab.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				e.preventDefault();
				setTab("ask");
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	const runAsk = (q: string) => {
		const trimmed = q.trim();
		if (trimmed.length === 0 || ask.isPending) return;
		setActiveCite(null);
		setLastAsked(trimmed);
		setRecent((prev) => [trimmed, ...prev.filter((x) => x !== trimmed)].slice(0, 5));
		ask.mutate({ question: trimmed });
	};

	// THE citation spine — one handler for answer markers + rail "View in document".
	const openCitation = (c: KbCitation) => {
		setActiveCite(c.n);
		setDrawer({ documentId: c.documentId, chunkIndex: c.chunkIndex, score: c.score });
	};

	const notFound = !workspacesLoading && workspaces !== undefined && !workspace;
	if (notFound) {
		return (
			<>
				<PageHeader icon={Library} title="Workspace not found" />
				<EmptyState
					icon={Library}
					title="This workspace doesn't exist"
					body="It may have been deleted. Head back to your workspaces."
					primary={
						<Link to="/kb">
							<Button variant="secondary">
								<ArrowLeft className="size-4" />
								Back to workspaces
							</Button>
						</Link>
					}
				/>
			</>
		);
	}

	const docCount = stats?.documentCount ?? documents?.total ?? 0;
	const totalChunks = stats?.totalChunks ?? 0;
	const typeEntries = stats
		? (Object.entries(stats.byType) as [DocType, number][]).filter(([, n]) => n > 0)
		: [];
	const indexing = stats ? stats.byStatus.pending + stats.byStatus.processing : 0;

	// Ask-derived view model. A null answer or no sources → the no-grounded state.
	const result = ask.data ?? null;
	const noGrounded = !!result && (!result.answer || result.sources.length === 0);
	const answer =
		result?.answer && result.sources.length > 0
			? deriveAnswer(result.answer, result.sources)
			: null;
	const citations = answer?.citations ?? [];

	// Documents tab filtering.
	const filteredDocs = (documents?.items ?? []).filter((d) => {
		const matchesType = typeFilter === "all" || d.docType === typeFilter;
		const matchesSearch = d.filename.toLowerCase().includes(search.trim().toLowerCase());
		return matchesType && matchesSearch;
	});

	const openDocument = (doc: KbDocument) => {
		setActiveCite(null);
		setDrawer({ documentId: doc.id, chunkIndex: 0 });
	};

	return (
		<>
			<PageHeader
				eyebrow="KNOWLEDGE BASE"
				icon={Library}
				title={workspace?.name ?? "Loading…"}
				subtitle={
					docCount > 0
						? `${docCount} document${docCount === 1 ? "" : "s"} · ${totalChunks.toLocaleString()} chunks${workspace ? ` · updated ${timeAgo(workspace.updatedAt)}` : ""}`
						: "Upload documents to index"
				}
				actions={
					<>
						<Button variant="secondary" onClick={() => setTab("docs")}>
							<UploadCloud className="size-3.5" />
							Upload
						</Button>
						<Button onClick={() => setTab("ask")}>
							<Sparkles className="size-3.5" />
							Ask
							<kbd className="ml-1 rounded bg-on-primary/15 px-1 text-[10px]">⌘K</kbd>
						</Button>
					</>
				}
				tabs={
					<>
						<PageTab active={tab === "docs"} onClick={() => setTab("docs")}>
							Documents
						</PageTab>
						<PageTab active={tab === "ask"} onClick={() => setTab("ask")}>
							Ask
						</PageTab>
					</>
				}
			/>

			{tab === "docs" ? (
				<>
					<Toolbar
						left={
							<>
								<div className="relative">
									<Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-subtle" />
									<Input
										value={search}
										onChange={(e) => setSearch(e.target.value)}
										placeholder="Search documents…"
										className="h-7 w-56 pl-7 text-body-sm"
									/>
								</div>
								{TYPE_FILTERS.map((t) => (
									<Pill
										key={t.key}
										active={typeFilter === t.key}
										onClick={() => setTypeFilter(t.key)}
									>
										{t.label}
									</Pill>
								))}
							</>
						}
						right={
							<span className="text-meta text-ink-subtle">
								{docCount} document{docCount === 1 ? "" : "s"} ·{" "}
								{indexing > 0 ? `${indexing} indexing` : "all indexed"}
							</span>
						}
					/>
					<div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
						{documentsLoading ? (
							<div className="space-y-2">
								{Array.from({ length: 4 }).map((_, i) => (
									// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
									<Skeleton key={i} h={44} className="block w-full" />
								))}
							</div>
						) : docCount === 0 ? (
							<div className="mx-auto max-w-xl py-8">
								<EmptyState
									icon={UploadCloud}
									title="Add documents to get started"
									body="Drop transcripts, notes or PDFs — we index them into searchable chunks so you can ask grounded questions."
								/>
								<UploadDropzone workspaceId={workspaceId} />
							</div>
						) : (
							<div className="space-y-5">
								{indexing > 0 ? (
									<div className="flex items-center gap-3 rounded-lg border border-hairline bg-surface-1 px-3.5 py-2.5">
										<span className="inline-flex size-6 flex-none items-center justify-center rounded-md bg-surface-2">
											<Loader2 className="size-3.5 animate-spin text-warning" />
										</span>
										<span className="text-body-sm text-foreground">
											Indexing {indexing} document{indexing === 1 ? "" : "s"}
										</span>
										<span className="text-meta text-ink-subtle">
											— they become askable as soon as they finish.
										</span>
									</div>
								) : null}
								<div className="grid grid-cols-[1fr_300px] gap-4">
									<div className="grid grid-cols-3 gap-3">
										<StatCard
											icon={FileText}
											label="Documents"
											value={docCount}
											sub={indexing > 0 ? `${indexing} indexing` : "all indexed"}
										/>
										<StatCard
											icon={Layers}
											label="Chunks"
											value={totalChunks.toLocaleString()}
											sub={docCount > 0 ? `avg ${Math.round(totalChunks / docCount)} / doc` : "—"}
										/>
										<StatCard
											icon={List}
											label="File types"
											value={typeEntries.length}
											sub={
												typeEntries
													.map(([t, n]) => `${n} ${DOC_TYPE_META[t].label.toLowerCase()}`)
													.join(" · ") || "—"
											}
										/>
									</div>
									<UploadDropzone workspaceId={workspaceId} compact />
								</div>

								{filteredDocs.length > 0 ? (
									<DocumentTable
										documents={filteredDocs}
										onOpen={openDocument}
										onDelete={(id) => deleteDocument.mutate(id)}
									/>
								) : (
									<EmptyState icon={Search} small title="No documents match your filters" />
								)}
							</div>
						)}
					</div>
				</>
			) : citations.length > 0 ? (
				<div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_360px] divide-x divide-hairline overflow-hidden">
					<AskPanel
						question={question}
						onQuestionChange={setQuestion}
						onSubmit={() => runAsk(question)}
						isPending={ask.isPending}
						docCount={docCount}
						recent={recent}
						onPickQuestion={(q) => {
							setQuestion(q);
							runAsk(q);
						}}
						answer={answer}
						noGrounded={noGrounded}
						error={ask.isError ? (ask.error as Error).message : null}
						activeCite={activeCite}
						onCite={openCitation}
						onRegenerate={() => runAsk(lastAsked)}
					/>
					<SourceRail
						citations={citations}
						activeCite={activeCite}
						onActivate={(c) => setActiveCite(c.n)}
						onView={openCitation}
					/>
				</div>
			) : (
				<div className="min-h-0 flex-1 overflow-hidden">
					<AskPanel
						question={question}
						onQuestionChange={setQuestion}
						onSubmit={() => runAsk(question)}
						isPending={ask.isPending}
						docCount={docCount}
						recent={recent}
						onPickQuestion={(q) => {
							setQuestion(q);
							runAsk(q);
						}}
						answer={answer}
						noGrounded={noGrounded}
						error={ask.isError ? (ask.error as Error).message : null}
						activeCite={activeCite}
						onCite={openCitation}
						onRegenerate={() => runAsk(lastAsked)}
					/>
				</div>
			)}

			{drawer ? (
				<SourceDrawer
					documentId={drawer.documentId}
					citedChunkIndex={drawer.chunkIndex}
					citedScore={drawer.score}
					onClose={() => setDrawer(null)}
				/>
			) : null}
		</>
	);
}
